import {
    isValidElement,
    type ReactNode,
    type FC,
    memo
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type {
    DataTableCardProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useDataTableContext
} from "../../context";
import type {
    IDataTableColumnBase
} from "../../type";
import {
    getDataTableCardRole
} from "../../util";
import StateLayer from "../../../stateLayer";
import CheckBox from "../../../checkBox";
import Text from "../../../text";

const DataTableCard: FC<DataTableCardProps> = ({
    customTheme: customThemeProp,
    isSelectionMode,
    isSelected,
    layout,
    row
}) => {
    const context = useDataTableContext({
        part: "Content"
    });

    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const {
        subtitle: subtitleDynamicStyle,
        surface: surfaceDynamicStyle,
        fields: fieldsDynamicStyle,
        header: headerDynamicStyle,
        titles: titlesDynamicStyle,
        field: fieldDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        shadowAlpha,
        elevations,
        isSelected,
        radiuses,
        borders,
        colors,
        spaces
    });

    if(!context) {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const isSelectable = context.selectionBehaviour !== "none";

    const isPressable = isSelectable || !!context.onRowPress;

    const handlePress = () => {
        if(isSelectable && isSelectionMode) {
            context.handleToggleRow(row.id);

            return;
        }

        context.onRowPress?.(row.id);
    };

    const handleLongPress = () => {
        if(isSelectable) {
            context.handleToggleRow(row.id);
        }
    };

    const renderNode = (node: ReactNode, variant: "titleSmallSize" | "bodySmallSize" | "bodyMediumSize" | "titleMediumSize", color: "high" | "mid", isNumeric = false) => {
        if(isValidElement(node) || (typeof node !== "string" && typeof node !== "number")) {
            return node;
        }

        return <Text
            style={isNumeric ? stylesheet.amount : undefined}
            customTheme={currentTheme}
            variant={variant}
            color={color}
        >
            {node}
        </Text>;
    };

    const renderSubtitle = () => {
        if(layout.subtitles.length === 0) {
            return null;
        }

        const nodes = layout.subtitles.map((column) => row.cells[column.id]);

        const isText = nodes.every((node) => typeof node === "string" || typeof node === "number");

        if(isText) {
            return <Text
                customTheme={currentTheme}
                variant="bodySmallSize"
                color="mid"
            >
                {
                    layout.subtitles.map((column, index) => index === 0 ? String(row.cells[column.id]) : localize("data-table-card-subtitle", [
                        column.header,
                        String(row.cells[column.id])
                    ])).join(" · ")
                }
            </Text>;
        }

        return <View
            style={[
                stylesheet.subtitle,
                subtitleDynamicStyle
            ]}
        >
            {
                layout.subtitles.map((column) => <View
                    key={column.id}
                >
                    {renderNode(row.cells[column.id], "bodySmallSize", "mid")}
                </View>)
            }
        </View>;
    };

    const renderField = (column: IDataTableColumnBase) => {
        const isAmount = getDataTableCardRole(column) === "amount";

        const isEnd = isAmount || !!column.isNumeric;

        return <View
            style={[
                stylesheet.field,
                isEnd && stylesheet.fieldEnd,
                fieldDynamicStyle
            ]}
            key={column.id}
        >
            <Text
                customTheme={currentTheme}
                variant="labelSmallSize"
                color="low"
            >
                {column.header}
            </Text>
            {
                isAmount ?
                    renderNode(row.cells[column.id], "titleMediumSize", "high", true)
                :
                    renderNode(row.cells[column.id], "bodyMediumSize", "high", !!column.isNumeric)
            }
        </View>;
    };

    return <View
        style={[
            stylesheet.surface,
            surfaceDynamicStyle
        ]}
        role="listitem"
    >
        <StateLayer
            isHovered={isPressable && isHovered}
            isPressed={isPressable && isPressed}
            borderRadius={radiuses.surface}
            customTheme={currentTheme}
            borderWidth={borders.line}
            overlayColor="default"
        />
        {
            isPressable ?
                <Pressable
                    {...interactionHandlers}
                    onLongPress={handleLongPress}
                    style={stylesheet.press}
                    aria-label={row.label}
                    onPress={handlePress}
                    role="button"
                />
            :
                null
        }
        <View
            style={[
                stylesheet.header,
                headerDynamicStyle
            ]}
        >
            {
                isSelectionMode ?
                    <CheckBox
                        accessibilityLabel={localize("data-table-select-row", [
                            row.label
                        ])}
                        onPress={() => context.handleToggleRow(row.id)}
                        customTheme={currentTheme}
                        isChecked={isSelected}
                    />
                :
                    null
            }
            <View
                style={[
                    stylesheet.titles,
                    titlesDynamicStyle
                ]}
            >
                {layout.title ? renderNode(row.cells[layout.title.id], "titleSmallSize", "high") : null}
                {renderSubtitle()}
            </View>
            {
                layout.action ?
                    <View
                        style={stylesheet.action}
                    >
                        {row.cells[layout.action.id]}
                    </View>
                :
                    null
            }
        </View>
        {
            layout.fields.length > 0 ?
                <View
                    style={[
                        stylesheet.fields,
                        fieldsDynamicStyle
                    ]}
                >
                    {layout.fields.map(renderField)}
                </View>
            :
                null
        }
    </View>;
};
export default memo(DataTableCard);
